/**
 * /scenarios - routed in Phase 4, empty state only.
 *
 * Section 12.5 says this page must call `GET /api/whatif/library` on mount and,
 * when the library is empty, explain that the sweep has not been run and give
 * the command - never let the user move a slider into a 409. In Phase 4 the
 * endpoint itself does not exist yet, so the page shows that same explanation
 * unconditionally. The saved scenarios below it are real: they come from
 * `/api/scenarios`, which does exist.
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { SlidersHorizontal } from 'lucide-react'

import { api } from '../api/client'

export default function Scenarios() {
  const scenarios = useQuery({ queryKey: ['scenarios'], queryFn: api.scenarios })

  return (
    <div className="mx-auto max-w-3xl p-8">
      <div className="flex items-center gap-2 text-slate-300">
        <SlidersHorizontal className="h-4 w-4" />
        <h2 className="text-sm font-medium">What-if scenarios</h2>
      </div>

      <div className="mt-4 rounded border border-slate-800 bg-slate-900 p-4">
        <h3 className="text-xs font-medium text-slate-200">
          The scenario library has not been built
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">
          The what-if sliders interpolate over a precomputed library of solver runs
          so they can answer in seconds; a full run cannot. Building it is a
          parameter sweep &mdash; eight runs at 360 m, roughly half an hour &mdash;
          and the interpolator that reads it arrives in Phase 5.
        </p>
        <code className="mt-3 block rounded border border-slate-800 bg-slate-950 p-2 text-12 text-slate-300">
          python -m backend.pipeline.cli sweep
        </code>
      </div>

      <h3 className="mt-6 text-xs font-medium uppercase tracking-wider text-slate-500">
        Saved scenarios
      </h3>

      {scenarios.isPending && (
        <p className="mt-2 text-sm text-slate-500">Loading&hellip;</p>
      )}
      {scenarios.isError && (
        <p className="mt-2 text-sm text-rose-400">
          {scenarios.error instanceof Error
            ? scenarios.error.message
            : 'Could not load scenarios'}
        </p>
      )}
      {scenarios.data?.length === 0 && (
        <p className="mt-2 text-sm text-slate-500">
          None yet.{' '}
          <Link to="/setup" className="text-sky-400 hover:underline">
            Set one up
          </Link>
          .
        </p>
      )}

      {scenarios.data && scenarios.data.length > 0 && (
        <table className="mt-2 w-full text-left text-xs">
          <thead className="text-12 uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-1.5 pr-3 font-medium">Name</th>
              <th className="py-1.5 pr-3 font-medium">Breach width</th>
              <th className="py-1.5 pr-3 font-medium">Formation</th>
              <th className="py-1.5 pr-3 font-medium">Level</th>
              <th className="py-1.5 font-medium">Grid</th>
            </tr>
          </thead>
          <tbody className="text-slate-300">
            {scenarios.data.map((scenario) => (
              <tr key={scenario.id} className="border-t border-slate-800">
                <td className="py-1.5 pr-3">{scenario.name}</td>
                <td className="py-1.5 pr-3">
                  {scenario.breach_width_m
                    ? `${scenario.breach_width_m.toFixed(0)} m`
                    : 'Froehlich'}
                </td>
                <td className="py-1.5 pr-3">
                  {scenario.breach_formation_time_s
                    ? `${(scenario.breach_formation_time_s / 60).toFixed(0)} min`
                    : 'Froehlich'}
                </td>
                <td className="py-1.5 pr-3">
                  {scenario.reservoir_level_m.toFixed(2)} m
                </td>
                <td className="py-1.5">{scenario.grid_resolution_m} m</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
